import {
  Target, DollarSign, Route, Gauge, TrendingUp,
  Compass, ClipboardCheck, Layers, Rocket, RefreshCcw,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Framework", href: "#framework" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Recruitment", href: "#recruitment" },
  { label: "Contact", href: "#contact" },
];

// EDITABLE: replace with real client / partner logos.
export const TRUST_LOGOS = [
  "AWS", "Microsoft Azure", "Google Cloud", "Salesforce",
  "BFSI Leaders", "SaaS / LMS", "IT Services", "Digital Services",
];

export const PROBLEMS = [
  {
    pain: "Stuck in cost-arbitrage pricing",
    reframe:
      "We reposition your offer around domain expertise and business outcomes — so you command premium ACV instead of competing on day-rate.",
  },
  {
    pain: "No repeatable enterprise sales motion",
    reframe:
      "We install MEDDPICC-grade playbooks, discovery scripts and deal-desk rhythms that turn heroics into a predictable revenue engine.",
  },
  {
    pain: "Pipeline that doesn't convert internationally",
    reframe:
      "We calibrate your ICP and route-to-market for US, UK/Europe, Middle East and APAC — building pipeline that actually closes across borders.",
  },
];

export const PILLARS = [
  {
    n: "01",
    icon: Target,
    name: "Market Intelligence & ICP Calibration",
    line: "Know exactly who to sell to — and why they buy now.",
    points: [
      "TAM / SAM / SOM modeling to size the real opportunity",
      "ICP refinement across firmographics, technographics, funding status & trigger events",
      "Buyer persona mapping: Economic Buyer, Technical Evaluator, Internal Champion",
    ],
  },
  {
    n: "02",
    icon: DollarSign,
    name: "Value Offerings & Monetization Strategy",
    line: "Shift from selling effort to selling outcomes.",
    points: [
      "Value proposition localization — from cost-arbitrage to domain expertise & business outcomes",
      "Pricing & packaging architecture: fixed-fee, outcome-based, SLA, retainer",
      "Competitive battlecards that win the room",
    ],
  },
  {
    n: "03",
    icon: Route,
    name: "Route-to-Market (RTM) & Channel Strategy",
    line: "Reach Tier-1 accounts through the right doors.",
    points: [
      "Direct outbound engine: multi-touch ABM targeting Tier-1 accounts",
      "Strategic alliances & ecosystem co-selling — AWS, Azure, GCP, Salesforce, regional SIs",
      "Inbound, digital presence & thought leadership",
    ],
  },
  {
    n: "04",
    icon: Gauge,
    name: "Sales Execution & RevOps Architecture",
    line: "Turn qualification and governance into velocity.",
    points: [
      "MEDDPICC qualification standards embedded into every deal",
      "Enterprise sales playbooks: discovery scripts, objection handling, proposal templates",
      "Pipeline velocity & governance: CRM stage-gates and deal-desk rhythms",
    ],
  },
  {
    n: "05",
    icon: TrendingUp,
    name: "Customer Success & Account Expansion (NRR)",
    line: "Land small, expand relentlessly.",
    points: [
      "Land-and-expand playbook: 90-day onboarding, $50K pilots → $500K+ accounts",
      "NRR rhythms: executive QBRs, cross-sell / upsell matrices",
      "Customer health scoring to protect and grow the base",
    ],
  },
];

export const PROCESS = [
  { icon: Compass, step: "01", title: "Discovery Call", desc: "A focused conversation to understand your revenue goals, markets and constraints." },
  { icon: ClipboardCheck, step: "02", title: "Diagnostic", desc: "A structured assessment across the 5-category GTM questionnaire to expose gaps." },
  { icon: Layers, step: "03", title: "Engagement Model", desc: "We select the right model — transformation, retainer or workshop — for your stage." },
  { icon: Rocket, step: "04", title: "Execution & Workshop", desc: "Playbook build, enablement and executive alignment intensives that ship." },
  { icon: RefreshCcw, step: "05", title: "Ongoing Advisory / NRR", desc: "Fractional CRO cadence, deal-desk governance and account-expansion rhythms." },
];

export const PRICING = [
  {
    name: "Fixed-Fee Transformation",
    scope: "8–12 week GTM strategy, playbook & enablement build",
    model: "Fixed Fee",
    highlight: false,
    features: [
      "Full 5-pillar GTM strategy build",
      "Enterprise sales playbooks & battlecards",
      "RevOps stage-gates & CRM design",
      "Team enablement & rollout",
    ],
  },
  {
    name: "Strategic Advisory Retainer",
    scope: "6-month minimum · fractional CRO / weekly deal desk",
    model: "Monthly Retainer + Success Bonus",
    highlight: true,
    features: [
      "Fractional CRO leadership",
      "Weekly deal-desk & pipeline governance",
      "Quarterly QBRs & NRR rhythms",
      "Success bonus aligned to outcomes",
    ],
  },
  {
    name: "Capability Workshop",
    scope: "1-day executive alignment & sales execution intensive",
    model: "Per-Workshop Engagement",
    highlight: false,
    features: [
      "Executive strategic alignment",
      "ICP calibration workshop",
      "MEDDPICC execution training",
      "Transformation roadmap output",
    ],
  },
];

export const FOUNDER_STATS = [
  { value: 18, suffix: "+", label: "Years in Sales Leadership" },
  { value: 1, prefix: "$", suffix: "M+", label: "Enterprise Deals Closed" },
  { value: 4, suffix: "", label: "Continents of GTM Reach" },
];

export const WORKSHOP_AGENDA = [
  "Strategic Context & Positioning",
  "ICP Calibration",
  "Route-to-Market Design",
  "Sales Execution / MEDDPICC",
  "Transformation Roadmap",
];

// Benchmark outcomes from the XSELL GTM transformation methodology.
export const RESULT_METRICS = [
  { value: "3.2x", label: "Pipeline Velocity Increase" },
  { value: "35%", label: "Lower Customer Acquisition Cost" },
  { value: "25%", label: "Shorter Sales Cycle" },
  { value: "40%", label: "Larger Enterprise Deal Size" },
];

export const AUDIT = {
  title: "Stop guessing why your revenue has plateaued.",
  body: "Our GTM Strategy Audit is a clinical 30-minute deep dive into your current sales engine. We'll map your existing funnel, identify the top three friction points stalling your growth, and provide a high-level roadmap to unblock your path to market leadership.",
  kicker: "No fluff — just a clear, actionable diagnosis of your revenue leaks.",
  steps: [
    { title: "Map your funnel", desc: "A full read of your current sales engine, stage by stage." },
    { title: "Isolate 3 friction points", desc: "The top blockers quietly stalling your growth today." },
    { title: "High-level roadmap", desc: "A clear path to unblock your route to market leadership." },
  ],
};

// EDITABLE placeholders — replace with real client testimonials.
export const TESTIMONIALS = [
  { quote: "Placeholder — add a client quote about pipeline transformation and enterprise wins here.", name: "Client Name", role: "CRO, Technology Firm" },
  { quote: "Placeholder — add a client quote about repositioning away from arbitrage pricing here.", name: "Client Name", role: "Founder & CEO" },
];

export const INSIGHTS = [
  {
    slug: "gtm-5-pillar-framework",
    tag: "Framework",
    title: "The 5-Pillar GTM Framework, Explained",
    read: "8 min read",
    excerpt:
      "How market intelligence, monetization, route-to-market, sales execution and account expansion combine into one repeatable revenue engine.",
    content: [
      { type: "p", text: "Most technology and B2B services firms don't have a revenue problem — they have a system problem. Pipeline arrives in bursts, deals close on heroics, and forecasts swing wildly quarter to quarter. The 5-Pillar GTM Framework replaces that unpredictability with an integrated commercial architecture that compounds." },
      { type: "p", text: "Each pillar solves a distinct failure mode. Together, they turn go-to-market from a series of disconnected campaigns into a governed, metrics-driven engine built for international and enterprise expansion." },
      { type: "h", text: "1. Market Intelligence & ICP Calibration" },
      { type: "p", text: "Everything starts with knowing exactly who to sell to and why they buy now. We size the real opportunity with TAM/SAM/SOM modeling, then sharpen the Ideal Customer Profile across firmographics, technographics, funding status and trigger events — and map the buying committee: Economic Buyer, Technical Evaluator and Internal Champion." },
      { type: "h", text: "2. Value Offerings & Monetization Strategy" },
      { type: "p", text: "The shift from selling effort to selling outcomes lives here. We localize the value proposition away from cost-arbitrage toward domain expertise and business results, then architect pricing and packaging — fixed-fee, outcome-based, SLA and retainer — supported by competitive battlecards that win the room." },
      { type: "h", text: "3. Route-to-Market & Channel Strategy" },
      { type: "p", text: "Reaching Tier-1 accounts requires the right doors. A direct outbound engine (multi-touch ABM) works alongside strategic alliances and ecosystem co-selling with AWS, Azure, GCP, Salesforce and regional SIs — amplified by inbound presence and thought leadership." },
      { type: "h", text: "4. Sales Execution & RevOps Architecture" },
      { type: "p", text: "Qualification and governance become velocity. We embed MEDDPICC into every deal, equip sellers with playbooks — discovery scripts, objection handling, proposal templates — and install CRM stage-gates and deal-desk rhythms that keep pipeline honest." },
      { type: "h", text: "5. Customer Success & Account Expansion (NRR)" },
      { type: "p", text: "The cheapest revenue is expansion revenue. A land-and-expand playbook turns $50K pilots into $500K+ accounts, while executive QBRs, cross-sell/upsell matrices and customer health scoring protect and grow the base." },
      { type: "p", text: "Installed together, the five pillars typically drive materially faster pipeline velocity, lower customer acquisition cost and larger enterprise deal sizes — the outcomes that actually move the revenue needle." },
    ],
  },
  {
    slug: "meddpicc-in-practice",
    tag: "RevOps",
    title: "MEDDPICC in Practice: Qualifying Enterprise Deals",
    read: "6 min read",
    excerpt:
      "A field guide to institutionalizing MEDDPICC so your team forecasts with 90%+ accuracy and stops chasing deals that never close.",
    content: [
      { type: "p", text: "MEDDPICC isn't a form to fill in after a deal is won — it's the operating discipline that tells you, early, whether a deal is real. Done well, it lifts forecast accuracy above 90% and frees your best reps from chasing ghosts." },
      { type: "p", text: "Here's what each element means in practice, and the questions that separate a qualified opportunity from wishful thinking." },
      { type: "h", text: "Metrics" },
      { type: "p", text: "What quantified business outcome will the buyer get? If you can't state the metric — hours saved, revenue gained, risk reduced — you don't yet have a business case." },
      { type: "h", text: "Economic Buyer" },
      { type: "p", text: "Have you met the person who can spend outside an approved budget? Champions open doors; economic buyers sign. Confusing the two is the most common cause of slipped deals." },
      { type: "h", text: "Decision Criteria & Decision Process" },
      { type: "p", text: "Know both the 'what' and the 'how': the criteria the buyer will judge you on, and the exact steps — technical, legal, procurement — the deal must pass through to close." },
      { type: "h", text: "Paper Process" },
      { type: "p", text: "The paperwork kills more late-stage deals than competitors do. Map security review, MSA redlines and procurement timelines before you forecast a close date." },
      { type: "h", text: "Identify Pain, Champion & Competition" },
      { type: "p", text: "Anchor to compelling pain, cultivate a champion who sells for you internally, and understand the competition — including the status quo and 'do nothing.'" },
      { type: "p", text: "The goal isn't to memorize eight letters. It's to build a shared language and stage-gate rhythm so every deal review asks the same hard questions — and your pipeline reflects reality." },
    ],
  },
  {
    slug: "land-and-expand",
    tag: "Expansion",
    title: "Land-and-Expand: Turning $50K Pilots into $500K Accounts",
    read: "7 min read",
    excerpt:
      "The post-sale motion that compounds: a 90-day onboarding rhythm, executive QBRs and an expansion matrix that grows NRR.",
    content: [
      { type: "p", text: "Winning the logo is the beginning, not the finish line. The firms that scale fastest treat the first $50K deal as a foothold — and run a deliberate motion to expand it into a $500K+ relationship." },
      { type: "h", text: "Land small, but land deliberately" },
      { type: "p", text: "A well-scoped pilot should be small enough to say yes to and structured to prove one high-value outcome. The pilot's success criteria are the seeds of the expansion case." },
      { type: "h", text: "The 90-day onboarding rhythm" },
      { type: "p", text: "The first 90 days decide the next three years. A structured onboarding — clear milestones, an executive sponsor on both sides, and an early value review — turns a new customer into a reference and a renewal into a formality." },
      { type: "h", text: "Executive QBRs" },
      { type: "p", text: "Quarterly business reviews aren't status updates; they're expansion meetings. Tie the conversation to the customer's business metrics, surface new use cases, and always leave with a mutually agreed next step." },
      { type: "h", text: "The cross-sell / upsell matrix" },
      { type: "p", text: "Map every account against adjacent products, teams and geographies. The matrix makes expansion systematic rather than opportunistic — and gives leadership a clear view of white space." },
      { type: "h", text: "Health scoring protects the base" },
      { type: "p", text: "Net Revenue Retention starts with not losing what you have. Customer health scoring — product usage, sentiment, executive engagement — flags risk early enough to act." },
      { type: "p", text: "Run this motion consistently and expansion becomes your most efficient growth channel: higher win rates, lower acquisition cost, and NRR that carries the plan." },
    ],
  },
];

export const REVENUE_GOALS = [
  "Under $1M ARR",
  "$1M – $5M ARR",
  "$5M – $20M ARR",
  "$20M+ ARR",
  "Entering a new international market",
];

// EDITABLE: replace with your real Calendly booking link.
export const CALENDLY_URL = "https://calendly.com/xsell-solutions/discovery-call";
